/**
 * Where each figure LIVES in a record (SPEC-002, SPEC-007).
 *
 * The addressing table and nothing else: which path under `specs` holds the top
 * speed, how many decimals it is published to, and whether it has a unit at all.
 * Split from `carProperties.js` at the seam the coding guidelines name —
 * mechanism against policy. That file is about PRESENTING a figure, and it has
 * grown a whole vocabulary for it: rows, labels, units, currency, dated labels.
 * This is the part underneath that has no opinion about any of it.
 *
 * The split is not tidiness. `domain/game` compares two cars on one figure and
 * needs the NUMBER — it has no use for an em dash, a unit key or a formatted
 * string, and a game module importing the spec sheet's presentation layer to
 * reach `specs.zeroToHundred.seconds` would be reaching through two concerns to
 * get at one. What it must not do is write that path down a second time: two
 * spellings of where a figure lives is how a catalogue change breaks one screen
 * and not the other.
 *
 * Pure. It imports one thing — the standing-distance marks, which are a closed
 * set and live with the other closed sets — and nothing else in the tree.
 */

import { QUARTER_MILE_MARK } from './carTraceMarks';

/**
 * Where each figure lives in a record, and how many decimals it is worth.
 *
 * `zeroToHundred` is the only one with a decimal, and it needs a fixed one: 4.1
 * and 5 next to each other on the same plate read as different kinds of
 * measurement, so 5 is drawn as `5.0`.
 *
 * `production` is the only one with no unit, and saying so here is not tidiness:
 * a unit key is COMPOSED (`cars.spec.production.unit`), so a figure that names
 * one the catalogue does not carry prints the key itself beside the number —
 * "1,311 cars.spec.production.unit" on the spec sheet. It has no unit to name
 * because it is a count rather than a measurement, and the label already says
 * what is being counted.
 */
export const FIGURES = {
    topSpeed: { valueOf: (specs) => specs?.topSpeed?.kmh },
    quarterMile: { valueOf: (specs) => quarterMilePoint(specs)?.seconds, fractionDigits: 1 },
    power: { valueOf: (specs) => specs?.power?.hp },
    torque: { valueOf: (specs) => specs?.torque?.nm },
    zeroToHundred: { valueOf: (specs) => specs?.zeroToHundred?.seconds, fractionDigits: 1 },
    displacement: { valueOf: (specs) => specs?.engine?.displacementCc },
    weight: { valueOf: (specs) => specs?.weightKg },
    length: { valueOf: (specs) => specs?.lengthMm },
    // A COUNT, and it exists beside `engineConfiguration` rather than inside it
    // because the two answer different questions. "V-12" is a word and reaches
    // the screen as a message key; twelve is a NUMBER, and a number is the only
    // thing two cars can be compared on. The duel asks which engine has more of
    // them (SPEC-007), and the Wankel answers null — which is why the RX-7
    // simply never appears under that question rather than appearing as a zero.
    cylinders: { valueOf: (specs) => specs?.engine?.cylinders, hasUnit: false },
    production: { valueOf: (specs) => specs?.productionUnits, hasUnit: false },
    // DERIVED, and the only one here that is. Horsepower per tonne is the
    // figure that explains most of the others — it is why a 1,150 kg Yellowbird
    // beats a 1,450 kg 959 to 100 km/h on less power — and the catalogue carries
    // both halves of it already. It arrived for the duel's reveal and was kept
    // off the spec table then, on the argument that widening that table was
    // SPEC-002's change rather than SPEC-007's; SPEC-002 has since made it, and
    // the sheet draws this row where the period sheets drew lb/bhp.
    powerToWeight: {
        valueOf: (specs) => {
            const hp = specs?.power?.hp;
            const kg = specs?.weightKg;

            return Number.isFinite(hp) && Number.isFinite(kg) && kg > 0
                ? Math.round(hp / (kg / 1000))
                : undefined;
        },
    },
};

/**
 * The mark that says which point is the standing quarter.
 *
 * Re-exported rather than declared: it moved to `carTraceMarks.js` when the set
 * of standing distances grew past one (SPEC-016 PR-16.3), and this module stays
 * an address its existing callers do not have to relearn. The argument for why
 * the figure lives in the trace at all is stated there.
 */
export { QUARTER_MILE_MARK };

/**
 * The trace point that is the standing quarter, or null.
 *
 * Null for fourteen of the twenty-four: eleven have no trace at all, and three
 * were tested by magazines that published no quarter. That is an absent figure rather
 * than a slow car, and the em dash the sheet draws says so.
 */
export function quarterMilePoint(specs) {
    const points = specs?.acceleration?.points;

    return points?.find((point) => point.mark === QUARTER_MILE_MARK) ?? null;
}

/** Is `spec` a figure this module knows how to read off a record? */
export function isFigure(spec) {
    return Object.prototype.hasOwnProperty.call(FIGURES, spec);
}

/**
 * The raw stored number, or `null` where the catalogue does not carry it.
 *
 * Null rather than the em dash `formatSpecNumber` answers with, because this is
 * the arithmetic seam rather than the presentation one: a caller comparing two
 * cars needs to know the figure is absent, not to be handed a dash it would
 * then have to test for.
 */
export function specValue(car, spec) {
    const value = isFigure(spec) ? FIGURES[spec].valueOf(car?.specs) : undefined;

    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * How many decimals this figure is published to.
 *
 * Exported because a caller doing arithmetic on two of these has to round to the
 * same precision the catalogue published — `5.9 - 5.6` is `0.2999999999999998`
 * in IEEE 754, and a comparison against a tenth-of-a-second threshold gets that
 * wrong in the one direction nobody would look for (SPEC-007 D-4).
 */
export function specFractionDigits(spec) {
    return (isFigure(spec) ? FIGURES[spec].fractionDigits : 0) ?? 0;
}

/**
 * Does this figure have a unit at all?
 *
 * Two of the eleven do not — `cylinders` and `production` are COUNTS rather
 * than measurements, and the label already says what is being counted. The
 * table above has always known that; what it did not have was a way for anybody
 * outside this module to ask.
 *
 * The absence was a live defect rather than a tidiness problem, and it had
 * already shipped once. A unit key is COMPOSED (`cars.spec.cylinders.unit`), so
 * `specUnitKey` is a string that is always truthy — a caller guarding on it
 * guards on nothing, and prints the key itself under the number. `figureRow`
 * gets this right by reading the table directly; the Overtake's card guarded on
 * the key and drew "8 cars.spec.cylinders.unit" for a whole round.
 */
export function specHasUnit(spec) {
    return isFigure(spec) ? FIGURES[spec].hasUnit !== false : false;
}
